import { useMemo } from 'react';
import { Icon } from '@iq/react-components';
import {
  usePanelDateDomain,
  useSelectedDateDomain,
  useActiveTimelineData,
} from './EventTimelineState';

import { USER_EVENTS, EVENT_TYPE_USER, EVENT_TYPE_DTMP, DTMP_SEVERITIES } from '../../../constants';
import config from '../../../config';

const getSeverityColor = (severityKey) => {
  switch (severityKey) {
    case 'critical':
    case 'error': {
      return '#F17679';
    }
    case 'warning': {
      return '#E9E34A';
    }
    default: {
      return '#3B6BF6';
    }
  }
};

const getEventLegends = (eventType) => {
  switch (eventType) {
    case EVENT_TYPE_USER: {
      return USER_EVENTS.map((eventMeta) => ({
        type: eventMeta.type,
        color: eventMeta.color,
        border: eventMeta.border,
        isCircle: true,
      }));
    }

    case EVENT_TYPE_DTMP: {
      return (
        Object.entries(DTMP_SEVERITIES)
          // filter out 'error' severity as it's not used in DTMP events.
          .filter(([key]) => key !== 'error')
          .map(([key, label]) => ({
            type: label,
            color: getSeverityColor(key),
            isCircle: false,
          }))
      );
    }

    // Defaults to MACH events
    default: {
      return [
        { type: 'Normal/Minor/Other', color: '#3B6BF6', isCircle: false },
        { type: 'Warning/Severe', color: '#E9E34A', isCircle: false },
        { type: 'Emergency', color: '#F17679', isCircle: false },
      ];
    }
  }
};

const EventLegends = ({ fullWidth, showLegend = true, eventType }) => {
  const selectedDateDomain = useSelectedDateDomain();
  const panelDateDomain = usePanelDateDomain();
  const currentPanelDateDomain = selectedDateDomain || panelDateDomain;
  const { response } = useActiveTimelineData();
  const EVENT_BUCKET_LIVE_QUERY_RANGE_MS = 7 * 24 * 60 * 60 * 1000; // live query (=< 1 week) means correct counts

  const eventLegends = getEventLegends(eventType);

  const getNumberOfEvents = () => {
    const events = response?.userEvents.length || 0;
    const bucketEvents =
      response?.iotBuckets.reduce((previous, current) => previous + current.numEvents, 0) || 0;
    const typeKey = eventType === EVENT_TYPE_USER ? 'user' : 'iot';
    return {
      user: events,
      iot: bucketEvents,
    }[typeKey];
  };

  const limitedUserEvents = response?.limitedUserEvents;

  const numberOfEvents = getNumberOfEvents();
  const singleEvent = numberOfEvents === 1;
  const multipleEvents = numberOfEvents > 1;

  const limitedEventsInfo = useMemo(
    () => (
      <div className={`limited-event-info`}>
        <p className="info-text">{numberOfEvents} events - not all visible</p>
        <div className="limited-event-info-trigger user">
          <Icon
            icon="he-help"
            className="event-info-icon"
            size="s"
          />
        </div>
        <div className="limited-event-info-tooltip">
          <p>
            {
              'A maximum of 100 unmodified events per recurring event series is shown for the selected time range – zoom in to see more events.'
            }
          </p>
        </div>
      </div>
    ),
    [numberOfEvents]
  );

  const aggregatedEventsInfo = useMemo(() => {
    const eventText = numberOfEvents === 1 ? 'event' : 'events';
    const countText =
      numberOfEvents > 0 ? `Approximately ${numberOfEvents} ${eventText}` : 'No events';
    return (
      <div className={`limited-event-info`}>
        <p className="info-text">{countText}</p>
        <div className="limited-event-info-trigger iot">
          <Icon
            icon="he-help"
            className="event-info-icon"
            size="s"
          />
        </div>
        <div className="limited-event-info-tooltip">
          <p>
            {'Event counts are derived from aggregated buckets which are refreshed at varying intervals. ' +
              'This can lead to temporary discrepancies between the actual number of events and the displayed count.'}
          </p>
          <p>Live queries resulting in accurate counts are run against time frames under 1 week.</p>
        </div>
      </div>
    );
  }, [numberOfEvents]);

  const shouldWarnLimitedEvents =
    multipleEvents && eventType === EVENT_TYPE_USER && limitedUserEvents;

  const shouldWarnAggregatedCount =
    config.onPremise &&
    eventType !== EVENT_TYPE_USER &&
    currentPanelDateDomain[1] - currentPanelDateDomain[0] > EVENT_BUCKET_LIVE_QUERY_RANGE_MS;

  let eventInfo = 'no events';

  if (shouldWarnLimitedEvents) {
    eventInfo = limitedEventsInfo;
  } else if (shouldWarnAggregatedCount) {
    eventInfo = aggregatedEventsInfo;
  } else if (multipleEvents) {
    eventInfo = `${numberOfEvents} events`;
  } else if (singleEvent) {
    eventInfo = `${numberOfEvents} event`;
  }

  if (currentPanelDateDomain.length > 0) {
    return (
      <div className={`event-timeline-info-header ${fullWidth ? 'over-list' : 'over-chart'}`}>
        {showLegend ? (
          <div className="event-timeline-legend">
            <ul>
              {eventLegends.map((eventMeta, index) => {
                const key = `${eventMeta.type}-${index}`;
                return (
                  <li
                    key={key}
                    className="graph-legend-item"
                  >
                    {eventMeta.isCircle ? (
                      <svg
                        width="12"
                        height="12"
                      >
                        <circle
                          r="5"
                          cx="6"
                          cy="6"
                          fill={eventMeta.color}
                          stroke={eventMeta.border}
                        />
                      </svg>
                    ) : (
                      <svg
                        width="10"
                        height="10"
                      >
                        <rect
                          width="10"
                          height="10"
                          rx="2"
                          ry="2"
                          fill={eventMeta.color}
                        />
                      </svg>
                    )}
                    {eventMeta.type}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        <div className="event-timeline-info">
          <div className="event-timeline-info">{eventInfo}</div>
        </div>
      </div>
    );
  }

  return null;
};

export default EventLegends;
